import React, { useState, useEffect } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Paper,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
  Chip,
} from '@mui/material';
import axios from 'axios';
import {CONFIG} from '../constants';

const ItemTable = () => {
  const [items, setItems] = useState([]);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [newItem, setNewItem] = useState({ id: '', secondary_id: '', description: '' });
  const [editingItem, setEditingItem] = useState(null);
  const [saving, setSaving] = useState(false);
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('id');

  useEffect(() => {
    fetchItems();
  }, []);

  const fetchItems = async () => {
    try {
      const response = await axios.get(`${CONFIG.backendUrl}/api/items`);
      setItems(response.data);
    } catch (error) {
      console.error('Error fetching items:', error);
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${CONFIG.backendUrl}/api/items/${id}`);
      setItems(items.filter((item) => item.id !== id));
    } catch (error) {
      console.error('Error deleting item:', error);
    }
  };

  const handleToggleActive = async (item) => {
    try {
      await axios.patch(`${CONFIG.backendUrl}/api/items/${item.id}/active`, {
        active: item.active === 0,
      });
      await fetchItems();
    } catch (error) {
      window.alert('Nem sikerült módosítani az állapotot. Próbáld újra!');
      console.error('Error updating active status:', error);
    }
  };

  const handleSaveItem = async () => {
    setSaving(true);
    try {
      await axios.put(`${CONFIG.backendUrl}/api/items/${editingItem.id}`, {
        secondary_id: editingItem.secondary_id,
        description: editingItem.description,
      });
      setEditingItem(null);
      await fetchItems();
    } catch (error) {
      window.alert('Nem sikerült menteni az adatokat. Próbáld újra!');
      console.error('Error saving item:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleAddItem = async () => {
    try {
      await axios.post(`${CONFIG.backendUrl}/api/items`, newItem, {
        headers: { 'Content-Type': 'application/json' },
      });
      setNewItem({ id: '', secondary_id: '', description: '' });
      fetchItems();
    } catch (error) {
      window.alert(`Ez a szám már létezik: ${newItem.id}`);
      console.error('Error adding item:', error);
    }
  };

  const openDeleteDialog = (id) => {
    setItemToDelete(id);
    setDeleteDialogOpen(true);
  };

  const formatLastSeen = (timestamp) => {
    if (!timestamp) {
      return 'Még soha';
    }

    return new Date(timestamp * 1000).toLocaleString('hu-HU');
  };

  const closeDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setItemToDelete(null);
  };

  const confirmDelete = () => {
    if (itemToDelete) {
      handleDelete(itemToDelete);
    }
    closeDeleteDialog();
  };

  const handleSort = (property) => {
    const isAscending = orderBy === property && order === 'asc';
    setOrder(isAscending ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const sortedItems = [...items].sort((a, b) => {
    const aValue = a[orderBy];
    const bValue = b[orderBy];

    if (orderBy === 'lastSeen') {
      if (!aValue && !bValue) return 0;
      if (!aValue) return 1;
      if (!bValue) return -1;
      return order === 'asc' ? aValue - bValue : bValue - aValue;
    }

    const comparison = String(aValue || '').localeCompare(String(bValue || ''), 'hu');
    return order === 'asc' ? comparison : -comparison;
  });

  const renderSortableHeader = (label, property) => (
    <TableCell sortDirection={orderBy === property ? order : false}>
      <TableSortLabel
        active={orderBy === property}
        direction={orderBy === property ? order : 'asc'}
        onClick={() => handleSort(property)}
      >
        {label}
      </TableSortLabel>
    </TableCell>
  );

  return (
    <TableContainer component={Paper}>
      <Table>
        <TableHead>
          <TableRow>
            {renderSortableHeader('ID', 'id')}
            {renderSortableHeader('Másodlagos ID', 'secondary_id')}
            {renderSortableHeader('Leírás', 'description')}
            {renderSortableHeader('Utoljára látva', 'lastSeen')}
            <TableCell>Állapot</TableCell>
            <TableCell>Műveletek</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {sortedItems.map((item) => (
            <TableRow key={item.id}>
              <TableCell>
                <div>{item.id}</div>
                {(!item.secondary_id?.trim() || !item.description?.trim()) && (
                  <Chip label="Kitöltendő adatok" size="small" color="warning" />
                )}
              </TableCell>
              <TableCell>{item.secondary_id}</TableCell>
              <TableCell>{item.description}</TableCell>
              <TableCell>{formatLastSeen(item.lastSeen)}</TableCell>
              <TableCell>
                <div>{item.active === 0 ? 'Inaktív' : 'Aktív'}</div>
                <Button variant="outlined" onClick={() => handleToggleActive(item)}>
                  {item.active === 0 ? 'Aktiválás' : 'Inaktiválás'}
                </Button>
              </TableCell>
              <TableCell>
                <Button onClick={() => setEditingItem({
                  ...item,
                  secondary_id: item.secondary_id || '',
                  description: item.description || '',
                })}>
                  Szerkesztés
                </Button>
                <Button color="error" variant="contained" onClick={() => openDeleteDialog(item.id)}>
                  Töröl
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <Dialog open={Boolean(editingItem)} onClose={() => !saving && setEditingItem(null)}>
        <DialogTitle>Adatok szerkesztése: {editingItem?.id}</DialogTitle>
        <DialogContent>
          <TextField
            label="Másodlagos ID"
            value={editingItem?.secondary_id || ''}
            onChange={(event) => setEditingItem({ ...editingItem, secondary_id: event.target.value })}
            margin="normal"
            fullWidth
            disabled={saving}
          />
          <TextField
            label="Leírás"
            value={editingItem?.description || ''}
            onChange={(event) => setEditingItem({ ...editingItem, description: event.target.value })}
            margin="normal"
            fullWidth
            disabled={saving}
          />
        </DialogContent>
        <DialogActions>
          <Button disabled={saving} onClick={() => setEditingItem(null)}>Elvetés</Button>
          <Button disabled={saving} onClick={handleSaveItem} variant="contained">Mentés</Button>
        </DialogActions>
      </Dialog>
      <Dialog open={deleteDialogOpen} onClose={closeDeleteDialog}>
        <DialogTitle>Törlés megerősítése</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Biztos, hogy törölni szeretnéd az elemet?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDeleteDialog} color="primary">
            Elvetés
          </Button>
          <Button onClick={confirmDelete} color="error">
            Jóváhagyás
          </Button>
        </DialogActions>
      </Dialog>
      <div style={{ padding: 16 }}>
        <TextField
          label="ID"
          value={newItem.id}
          onChange={(e) => setNewItem({ ...newItem, id: e.target.value })}
          margin="normal"
          variant="outlined"
        />
        <TextField
          label="Másodlagos ID"
          value={newItem.secondary_id}
          onChange={(e) => setNewItem({ ...newItem, secondary_id: e.target.value })}
          margin="normal"
          variant="outlined"
        />
        <TextField
          label="Leírás"
          value={newItem.description}
          onChange={(e) => setNewItem({ ...newItem, description: e.target.value })}
          margin="normal"
          variant="outlined"
        />
        <Button
          onClick={handleAddItem}
          variant="contained"
          color="primary"
          disabled={newItem.id == "" || newItem.id.length!=4 || !newItem.id.startsWith("F")}
          style={{ marginTop: 16 }}
        >
          Hozzáadás
        </Button>
      </div>
    </TableContainer>
  );
};

export default ItemTable;
